import { Icon } from '@chakra-ui/icons'
import {
  AspectRatio as ChakraAspectRatio,
  Box,
  Flex,
  HStack,
  Text,
  Tooltip,
  VStack,
  IconButton,
} from '@chakra-ui/react'
import numeral from 'numeral'
import type { ReactElement } from 'react'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { BsFillShieldLockFill } from 'react-icons/bs'
import { RiDraftLine } from 'react-icons/ri'
import styled from 'styled-components'
import {
  getExtensionFromFileTypeAndMimeType,
  mediaLibraryThumbnailTransformation,
} from '../../../../../utils/mediaLibrary'
import type { MediaLibrarySearchFileResponse } from '../../../../interfaces'
import type { MediaLibraryActionType } from '../../../../providers/MLProvider'
import { MediaLibraryActions } from '../../../../providers/MLProvider'
import { PublicLinksInfoContext } from '../../../../providers/PublicLinksInfoProvider'
import { getFileURL } from '../../../../../utils/mediaLibrary'
import { UserContext } from '../../../../providers/UserProvider'
import useGetFileUrlArguments from '../../../../hooks/useGetFileUrlArguments'
import { ContextMenuTrigger } from '../../ContextMenu'
import FilePreview from '../../FilePreview'
import ResponsiveTooltip from '../../../ResponsiveTooltip'
import { FiCopy, FiLink } from 'react-icons/fi'
import { CiEdit } from 'react-icons/ci'
import { GoLock } from 'react-icons/go'
import { useNavigateToDetail } from '../../../../hooks/useNavigateToDetail'
import { useSingleClickHandling } from '../../../../hooks/useSingleClickHandling'
import { useFetch } from 'use-http/dist/cjs/useFetch'
import { CachePolicies } from 'use-http/dist/cjs/types'

const AspectRatio = styled(ChakraAspectRatio)`
  & > img,
  & > video {
    object-fit: contain !important;
  }
`
interface FileElementProps {
  focusedIndex: { index: number; type: string; id: string } | null
  setSelectedAssetMap: { [key: string]: Function }
  selectedAssetMap: { [key: string]: Set<any> }
  setFocusedIndex: Function
  index: number
  getIdMap: { [key: string]: Function }
  dispatch: React.Dispatch<MediaLibraryActionType | MediaLibraryActionType[]>
  mediaCollectionRoute: boolean
  currentMediaCollection: string
  folderPath: string
  file: MediaLibrarySearchFileResponse
  gridTypeForDataset?: 'recent' | 'file' | 'folder'
  gridOrder: string[]
  assetCountMap: { [key: string]: number }
  route?: string
}

const NewFileElement = ({
  focusedIndex,
  setSelectedAssetMap,
  selectedAssetMap,
  setFocusedIndex,
  index,
  getIdMap,
  dispatch,
  mediaCollectionRoute,
  currentMediaCollection,
  folderPath,
  file,
  gridTypeForDataset,
  gridOrder,
  assetCountMap,
  route,
}: FileElementProps): ReactElement => {
  const {
    name,
    type,
    isPrivateFile,
    isPublished,
    size,
    height,
    width,
    versionInfo,
  } = file

  const { isPublicLink, clientInfo } = useContext(PublicLinksInfoContext)
  const { user } = useContext(UserContext)

  const getFileUrlDynamicArguments = useGetFileUrlArguments({
    clientInfo,
    user,
  })

  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const { t } = useTranslation()

  const openDetailPage = useNavigateToDetail()

  const singleClickHandler = useSingleClickHandling({
    gridOrder,
    assetCountMap,
    getIdMap,
    selectedAssetMap,
    setSelectedAssetMap,
    focusedIndex,
    setFocusedIndex,
  })

  const extension = getExtensionFromFileTypeAndMimeType(file)

  const gridType = gridTypeForDataset ?? 'file'
  const newId = getIdMap[gridType](index)
  const isFocused =
    focusedIndex?.index === index && focusedIndex?.type === gridType
  const isSelected = !!selectedAssetMap[gridType]?.has(newId)

  const getFinalUrl = async () => {
    let finalUrl = ''
    const data = await getFileURL({
      file,
      ...getFileUrlDynamicArguments,
      request,
    })
    if (data.includes('ik-s=') && data.includes('ik-auth-version')) {
      const url = new URL(data)
      url.searchParams.delete('ik-auth-version')
      url.searchParams.delete('ik-s')
      url.searchParams.delete('ik-t')
      finalUrl = url.toString()
    } else {
      finalUrl = data
    }
    return finalUrl
  }
  return (
    <ContextMenuTrigger
      onTrigger={event => {
        if (!isSelected) {
          dispatch([
            {
              type: MediaLibraryActions.SELECT_FILE,
              payload: {
                file,
                event,
              },
            },
            {
              type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
            },
          ])
          setFocusedIndex({ index: index, type: gridType, id: newId })
        }
      }}
      dispatch={dispatch}
    >
      {contextMenuProps => (
        <VStack
          borderWidth="1px"
          borderRadius="8px"
          backgroundColor={isSelected ? '#e6ecf8' : '#fff'}
          borderColor={isFocused ? '#0450d5' : '#e1e4eb'}
          spacing={0}
          cursor="pointer"
          onClick={event => {
            event.stopPropagation()
            event.preventDefault()
            singleClickHandler(gridType, index, newId, event)
            if(route  !== '/search'){
            dispatch([
              {
                type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
              },
            ])
          }
          }}
          onDoubleClick={event =>{
            dispatch([{
              type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
            },
          ])
            openDetailPage({
              mediaCollectionRoute,
              currentMediaCollection,
              file,
            })
          }
          }
          width="100%"
          overflow="hidden"
          justifyContent="flex-start"
          alignItems="flex-start"
          position="relative"
          {...contextMenuProps}
          data-index={index}
          data-grid-type={gridType}
          className="drag-selectable-element no-right-click-context-menu"
        >
          <Box
            width="100%"
            position="relative"
            padding={'4px'}
            borderBottomWidth={'1px'}
            borderColor="#e1e4eb"
          >
            <Flex
              position="absolute"
              bottom="8px"
              left="8px"
              zIndex={1}
              direction="row"
              alignItems="center"
              gap="4px"
            >
              {type === 'file-version' && (
                <Tooltip
                  label={versionInfo.name ?? 'Version Info'}
                  placement="top-start"
                  shouldWrapChildren
                  zIndex={100}
                >
                  <Box
                    backgroundColor="#f5f5f6"
                    fontSize="10px"
                    fontWeight="normal"
                    lineHeight="1.6"
                    textAlign="left"
                    color="#3b485e"
                    padding="4px 6px"
                    borderRadius="40px"
                  >
                    File version
                  </Box>
                </Tooltip>
              )}
              {!isPublicLink && !isPublished && (
                <Tooltip
                  label={t('draft', 'Draft')}
                  placement="top-start"
                  shouldWrapChildren
                  zIndex={100}
                >
                  <Box
                    backgroundColor="#f5f5f6"
                    borderRadius="40px"
                    padding="4px 6px"
                  >
                    <CiEdit color="#3b485e" />
                  </Box>
                </Tooltip>
              )}
              {!isPublicLink && isPrivateFile && (
                <Tooltip
                  label={t('private', 'Private')}
                  placement="top-start"
                  shouldWrapChildren
                  zIndex={100}
                >
                  <Box
                    backgroundColor="#f5f5f6"
                    borderRadius="40px"
                    padding="4px 6px"
                  >
                    <GoLock color="#3b485e" />
                  </Box>
                </Tooltip>
              )}
            </Flex>
            <Box borderRadius="4px" overflow="hidden">
              <AspectRatio width="100%" ratio={1 / 1}>
                <FilePreview
                  file={file}
                  folderPath={folderPath}
                  transformation={mediaLibraryThumbnailTransformation}
                />
              </AspectRatio>
            </Box>
          </Box>

          <VStack
            padding="8px 16px"
            width="100%"
            spacing="4px"
            justifyContent="flex-start"
            alignItems="flex-start"
          >
            <Flex
              direction="row"
              alignItems="center"
              width="100%"
              gap={'8px'}
              justifyContent="space-between"
            >
              <Tooltip label={name} placement="top-start">
                <Text
                  flex="1"
                  noOfLines={1}
                  color={'#0b1b36'}
                  fontWeight="600"
                  fontSize="14px"
                  textAlign="left"
                  whiteSpace="nowrap"
                >
                  {name}
                </Text>
              </Tooltip>
              <Flex
                direction="row"
                alignItems="center"
                marginTop="4px"
                justifyContent="right"
                flexShrink={0}
                gap="8px"
              >
                <ResponsiveTooltip
                  initialLabel={t('copy_name', 'Copy Name')}
                  successLabel={t('copied2', 'Copied')}
                  failureLabel={t('copy_failed', 'Failed to copy')}
                  textToCopy={name}
                >
                  <IconButton
                    padding={'0px'}
                    minWidth="unset"
                    icon={<Icon as={FiCopy} boxSize={4} />}
                    aria-label={t('copy_name', 'Copy name')}
                    color="#a0aec0"
                    variant="linkTwo"
                    size="sm"
                  />
                </ResponsiveTooltip>
                <ResponsiveTooltip
                  initialLabel={t('copy_url', 'Copy URL')}
                  successLabel={t('copied2', 'Copied')}
                  failureLabel={t('copy_failed', 'Failed to copy')}
                  getCopyText={getFinalUrl}
                >
                  <IconButton
                    minWidth="unset"
                    icon={<Icon as={FiLink} boxSize={4} />}
                    aria-label={t('copy_url', 'Copy URL')}
                    color="#a0aec0"
                    variant="linkTwo"
                    size="sm"
                  />
                </ResponsiveTooltip>
              </Flex>
            </Flex>
            <HStack
              spacing={0}
              width="full"
              justifyContent="space-between"
              whiteSpace="nowrap"
            >
              <Text
                color="#3b485e"
                fontSize="sm"
                noOfLines={1}
                whiteSpace="nowrap"
              >
                {extension ? `${extension.toUpperCase()} • ` : undefined}
                {numeral(size).format('0.00 ib').replace('i', '')}
                {height && width ? ` • ${width}x${height}` : null}
              </Text>
            </HStack>
          </VStack>
        </VStack>
      )}
    </ContextMenuTrigger>
  )
}

export default React.memo(NewFileElement)
